import { FlightSelectCallbackOptions } from '@fe/flight-layer';
import { SelectedWeatherItem } from '@fe/weather';
import React from 'react';
import { DrawingObjectCreateInfo, DrawingObjectUpdateDiff } from '../../components/drawing-object/types';
import { WeatherDetailsWindowArgs } from '../../components/map-interaction/context-menu/WeatherContextMenu';
import { AnalyzerRoute, MinimalAnalyzerRoute } from '../../components/route-analyzer/types';
import { FlightDetailsModel } from '../../flights/interop/flightDto';
import { FPMPlannedRouteResponse, FPMRouteResponse, FPMVerifyRouteResponse } from '../../messages/message-parser';
import {
    AirportArrDepRunways,
    AirportMetarInfo,
    AirportRvrInfo,
    FAIResponse,
    FlightFlownPointAdded,
    FlightRoutesUpdated
} from '../../services/actor-messages';
import type { FpmRequestFinished } from '../../services/integration/fpm-request-tracker';
import { MapContextMenuData, MapContextMenuInfo } from '../../services/map-interaction-service';
import { UDViewMapModel, UdViewModel, UDViewPlaneFilterSettingModel } from '../../udView/udview';
import { FlightDataEntryRequestBase, FlightEntryWindowType } from '../features/flight-data-entry/types';
import { LayersAircraftNavState, LayersNavState } from '../features/home/flyouts/LayersFlyout/LayersFlyout.types';
import { OpenNotesCommand } from '../features/home/flyouts/Notes';
import { HomeFlyoutTypes } from '../features/home/flyouts/types';
import { ScreenshotCommand } from '../features/home/layers/MapScreenshot';
import { PreviewInfo } from '../features/home/providers/PreviewProvider';

export type OpenFlyoutArgs =
    | { type: Exclude<HomeFlyoutTypes, HomeFlyoutTypes.layers> }
    | {
          type: HomeFlyoutTypes.layers;
          sub?:
              | {
                    type: LayersNavState.AircraftSettings;
                    sub?: LayersAircraftNavState;
                }
              | {
                    type: Exclude<LayersNavState, LayersNavState.AircraftSettings>;
                };
      };

export interface Messages {
    'route-analyzer-load-flight': number;
    'route-analyzer-load-routes': MinimalAnalyzerRoute[];
    'route-analyzer-edit-start': { id: string };
    'route-analyzer-edit-save': { route: AnalyzerRoute };
    'route-analyzer-edit-cancel': { original: AnalyzerRoute };
    'flight-selected': { details: FlightDetailsModel; options: FlightSelectCallbackOptions };
    'select-flight': { flightId: number; options: FlightSelectCallbackOptions };
    'unselect-flight': { flightId: number; options: FlightSelectCallbackOptions };
    'workspace.view-selected': { resolveViewLoaded: (value: unknown) => void; workspaceId: number; view: UdViewModel };
    'debug-gdf': { item: SelectedWeatherItem };
    'get-map-state': {
        workspaceId: number;
        viewId: string;
        result: (state: Pick<UDViewMapModel, 'lat' | 'lon' | 'zoom' | 'pitch' | 'bearing' | 'terrain'>) => void;
    };
    'update-end': object;
    'airports-changed': object;
    'context-menu': MapContextMenuInfo;
    'take-map-screenshot': ScreenshotCommand;
    'show-notes-window': OpenNotesCommand;
    'show-flight-entry-window': { type: FlightEntryWindowType; flightInfo: FlightDataEntryRequestBase };
    'show-notam-window': { notam: string };
    'show-runway-winds': { airportId: string; label: string; flightRunway?: string | null };
    'show-diversion': string;
    'fpm-route-response': FPMRouteResponse;
    'fpm-planned-route-response': FPMPlannedRouteResponse;
    'fpm-verify-route-response': FPMVerifyRouteResponse;
    'fpm-request-finished': FpmRequestFinished;
    'open-feedback': null;
    'open-command-center': null;
    'toggle-ai-chat': null;
    'dashboard-settings-open': null;
    'open-flyout': OpenFlyoutArgs;
    'toggle-constraints-list': null;
    'toggle-weather-product': { productId: number } | { groupId: number; fallbackProductId: number };
    'open-weather-settings': { category: number }; // TODO: replace with 'open-flyout'
    'add-new-filter': { template?: Partial<UDViewPlaneFilterSettingModel> };
    'alert-zoom-to-aircraft': { id: number };
    FAIResponse: FAIResponse;
    'airport-config-updated': AirportArrDepRunways;
    'airport-metar-updated': AirportMetarInfo;
    'airport-rvr-updated': AirportRvrInfo;
    'flight-routes-updated': FlightRoutesUpdated;
    'show-preview': PreviewInfo;
    'hide-preview': { id: string };
    'hide-all-previews': null;
    fullscreen: null;
    onbeforeunload: { shouldPreventDefault: boolean };
    'fast-forward-progress': number;
    'file-handle-changed': string;
    'weather-details-window-open': WeatherDetailsWindowArgs | null;
    'show-scratchpad-window': { flightId: number };
    'reload-all-weather': undefined;
    'flight-flown-point-added': FlightFlownPointAdded[];
    'tutorial-component': null | { type: 'flight-context-menu'; elementId: string };
    'draw.create': { drawObject: DrawingObjectCreateInfo };
    'diversion-record-updated': null;

    // Normally we just listen to store changes with useAppSelector but there's a whole bunch of code that depends on this event
    // with drawing object diff. So it was quicker to just do it this way.
    // This event is fired form drawing-object-update-middleware. Kinda neat btf
    // Also having a diff is useful because drawing objects can be big (polygon) so it's wasteful refreshing entire geojson source in maplibre or sending all of them the worker
    'drawing-objects-updated': DrawingObjectUpdateDiff;

    //internal
    'deferred-event-queued': keyof DeferredEvents;
}

type Listener<T extends keyof Messages> = (args: Messages[T]) => unknown;
export type EventBusSubscription = {
    unsubscribe: () => void;
};

export type EventItem = {
    [K in keyof Messages]: { name: K; data: Messages[K] };
}[keyof Messages];

export interface DeferredEvents {
    home?: EventItem[];
    selectedMap?: EventItem[];
}

class _EventBus {
    private listeners: { [K in keyof Messages]?: Listener<K>[] } = {};
    private deferredQueue: DeferredEvents = {};

    on<T extends keyof Messages>(type: T, listener: Listener<T>): EventBusSubscription {
        this.listeners[type] ??= [];
        this.listeners[type].push(listener);
        return {
            unsubscribe: () => this.off(type, listener)
        };
    }

    off<T extends keyof Messages>(type: T, listener: Listener<T>): void {
        const idx = this.listeners[type]?.indexOf(listener);
        if (idx === undefined || idx === -1) {
            return;
        }
        this.listeners[type]?.splice(idx, 1);
    }

    fire<T extends keyof Messages>(type: T, args: Messages[T]): void {
        for (const listener of this.listeners[type] ?? []) {
            listener(args as any);
        }
    }

    fireContextMenu(elements: MapContextMenuData[], event: MouseEvent) {
        this.fire('context-menu', {
            elements,
            event: event,
            view: event.view
        });
    }

    // Defer event to be dispatched later, e.g. when map is ready.
    // Use only when absolutely necessary (e.g. taking map screenshot from flight list before navigating to map)
    // Categories: home - events to be fired after navigation to home, selectedMap - events to be fired when selected map is ready
    defer<T extends keyof Messages>(category: keyof DeferredEvents, type: T, args: Messages[T]): void {
        this.deferredQueue[category] ??= [];
        this.deferredQueue[category].push({ name: type, data: args } as EventItem);
        this.fire('deferred-event-queued', category);
    }

    fireDeferredEvents(category: keyof DeferredEvents): void {
        const events = this.deferredQueue[category] ?? [];
        for (const event of events) {
            this.fire(event.name, event.data);
        }
        this.deferredQueue[category] = [];
    }
}

export const EventBus = new _EventBus();
export const EventContext = React.createContext<_EventBus>(EventBus);

export function useEventBus() {
    return React.useContext(EventContext);
}

export function useEvent<T extends keyof Messages>(type: T, listener: Listener<T>) {
    React.useEffect(() => {
        return EventBus.on(type, listener).unsubscribe;
    }, [type, listener]);
}
